/// web/src/features/pm/PmList.jsx
import React from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { collection, getDocs, orderBy, query, where } from 'firebase/firestore'

import { listCommunitiesFS } from '@/services/firestore/communityService'
import { updatePmTemplateFS } from '@/services/firestore/pmService'
import { createArchiveRequestTaskFS } from '@/services/firestore/taskService'
import { subscribeSysConfigFS } from '@/services/firestore/sysConfigService'
import { db } from '@/firebase'
import useSession from '@/hooks/useSession'
import ConfirmDialog from '../../components/ConfirmDialog.jsx'

/* ---------- shared helpers (align with PmNew) ---------- */

// near the top of each file (optional helper)
const HEADER_STYLE = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginTop: 14,
  marginBottom: 12,
}

function lc(s) {
  return (s || '').toString().trim().toLowerCase()
}

function chunk(arr, size = 10) {
  const out = []
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size))
  return out
}


function parseDateTime(dateStr, timeStr) {
  if (!dateStr) return null

  // handle "YYYY-MM-DD" and "MM/DD/YYYY"
  let y, m, d
  if (dateStr.includes('-')) {
    ;[y, m, d] = dateStr.split('-').map(Number)
  } else if (dateStr.includes('/')) {
    ;[m, d, y] = dateStr.split('/').map(Number)
  }

  if (!y || !m || !d) return null
  const [hh, mm] = (timeStr || '09:00').split(':').map(Number)
  return new Date(y, m - 1, d, hh || 0, mm || 0, 0, 0)
}

function addInterval(base, frequency, interval) {
  const iv = Number(interval) || 1
  const d = new Date(base.getTime())
  switch ((frequency || 'monthly').toLowerCase()) {
    case 'weekly':
      d.setDate(d.getDate() + 7 * iv)
      break
    case 'quarterly':
      d.setMonth(d.getMonth() + 3 * iv)
      break
    case 'yearly':
      d.setFullYear(d.getFullYear() + iv)
      break
    case 'monthly':
    default:
      d.setMonth(d.getMonth() + iv)
      break
  }
  return d
}

function computeNextOccurrence(schedule) {
  if (!schedule) return null
  const base = parseDateTime(schedule.startDate, schedule.timeOfDay)
  if (!base) return null
  const now = new Date()
  let next = base
  let safety = 0
  while (next < now && safety < 600) {
    next = addInterval(next, schedule.frequency, schedule.interval)
    safety++
  }
  return next
}

function formatDateTime(dt) {
  if (!dt) return ''
  const date = dt.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
  const time = dt.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit'
  })
  return `${date} ${time}`
}

// LIST ONLY: schedule without time, just frequency label
function formatScheduleShort(schedule) {
  if (!schedule) return '—'
  const freqMap = {
    weekly: 'Weekly',
    monthly: 'Monthly',
    quarterly: 'Quarterly',
    yearly: 'Yearly'
  }
  const freqKey = (schedule.frequency || '').toLowerCase()
  const label = freqMap[freqKey] || 'Custom'
  return label
}

// CSV helpers for export
function csvEscape(value) {
  if (value == null) return ''
  const str = String(value)
  // If it contains comma, quote or newline, wrap in quotes and escape quotes
  if (/[",\n]/.test(str)) {
    return '"' + str.replace(/"/g, '""') + '"'
  }
  return str
}

function buildPmCsvRows(pms) {
  const header = [
    'Name',
    'Community Name',
    'Community Code',
    'Status',
    'Frequency',
    'Start Date',
    'Time of Day',
    'Next Occurrence',
    'Description'
  ]

  const rows = pms.map(pm => {
    const schedule = pm.schedule || {}
    return [
      pm.name || '',
      pm.communityName || '',
      pm.communityCode || '',
      pm.isActive ? 'Active' : 'Inactive',
      (schedule.frequency || '').toString(),
      schedule.startDate || '',
      schedule.timeOfDay || '',
      pm.nextOccurrence ? formatDateTime(pm.nextOccurrence) : '',
      pm.description || ''
    ]
  })

  const all = [header, ...rows]
  return all.map(r => r.map(csvEscape).join(',')).join('\r\n')
}

/* ------------------------------------------------------------------ */

export default function PmList() {
  const nav = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const [communities, setCommunities] = React.useState([])
  const [templates, setTemplates] = React.useState([])
  const [loading, setLoading] = React.useState(true)
  const [err, setErr] = React.useState('')

  // filters (keep query-string friendly so deep links still work)
  const [statusFilter, setStatusFilter] = React.useState(
    searchParams.get('status') || 'all'
  )
  const [freqFilter, setFreqFilter] = React.useState(
    searchParams.get('freq') || 'all'
  )
  const [communityFilter, setCommunityFilter] = React.useState(
    searchParams.get('communityId') || ''
  )
  const [searchText, setSearchText] = React.useState(
    searchParams.get('q') || ''
  )
  const [showArchived, setShowArchived] = React.useState(
    searchParams.get('archived') === '1'
  )

  const sess = useSession()
  const isAdmin = React.useMemo(
    () => ['admin', 'sysconfig'].includes(lc(sess?.adminLevel)),
    [sess]
  )
  const myCommunities = React.useMemo(
    () => {
      if (sess?.allCommunities === true) return new Set()
      return new Set(
        (Array.isArray(sess?.communities) ? sess.communities : [])
          .filter(Boolean)
          .map(lc)
      )
    },
    [sess]
  )

  // custom confirm dialog state for soft delete / restore
  const [confirmToggleState, setConfirmToggleState] = React.useState({
    open: false,
    target: null, // pm row
    makeInactive: true
  })
  const [adminOnlyArchive, setAdminOnlyArchive] = React.useState(false)
  const [restrictedArchiveTarget, setRestrictedArchiveTarget] = React.useState(null)

  React.useEffect(() => {
    return subscribeSysConfigFS((next) => {
      setAdminOnlyArchive(next?.restrictions?.adminOnlyArchive === true)
    })
  }, [])

  // load communities + pmTemplates
  React.useEffect(() => {
    ;(async () => {
      try {
        setLoading(true)
        setErr('')

        const comms = await listCommunitiesFS()

        // Scope communities by session membership for non-admin users.
        const allCommunities = Array.isArray(comms) ? comms : []
        const scoped = allCommunities.filter(c => {
          if (isAdmin || sess?.allCommunities === true) return true
          if (!myCommunities.size) return false
          const code = lc(c.code || c.communityCode)
          const name = lc(c.communityName || c.name || c.code)
          return myCommunities.has(code) || myCommunities.has(name)
        })
        setCommunities(scoped)

        let docs = []
        if (isAdmin) {
          const snap = await getDocs(
            query(collection(db, 'pmTemplates'), orderBy('name', 'asc'))
          )
          docs = snap.docs
        } else {
          const scopedCodes = Array.from(
            new Set(
              scoped
                .map(c => c.code || c.communityCode)
                .filter(Boolean)
            )
          )

          if (scopedCodes.length) {
            for (const batch of chunk(scopedCodes, 10)) {
              const snap = await getDocs(
                query(
                  collection(db, 'pmTemplates'),
                  where('communityCode', 'in', batch)
                )
              )
              docs.push(...snap.docs)
            }
          }
        }

        const deduped = Array.from(
          new Map(docs.map(d => [d.id, d])).values()
        )

        const pmRows = deduped.map(d => {
          const data = d.data() || {}
          const schedule = data.schedule || {}
          const nextOccurrence = computeNextOccurrence(schedule)
          return {
            id: d.id,
            ...data,
            schedule,
            nextOccurrence
          }
        })

        pmRows.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
        setTemplates(pmRows)
      } catch (e) {
        console.error('[PmList] failed to load', e)
        setErr('Failed to load PM templates.')
      } finally {
        setLoading(false)
      }
    })()
  }, [isAdmin, myCommunities])

  const communityOptions = React.useMemo(() => {
    const base = (communities || []).map(c => ({
      code: c.code || c.communityCode,
      name: c.communityName || c.name || c.code
    }))

    // sort alphabetically by display name
    return base.sort((a, b) => a.name.localeCompare(b.name))
  }, [communities])

  /* ---------- filter + search ---------- */

  const filteredTemplates = React.useMemo(() => {
    const q = searchText.trim().toLowerCase()

    return (templates || []).filter(pm => {
      // status
      if (!showArchived && !pm.isActive) return false
      if (statusFilter === 'active' && !pm.isActive) return false
      if (statusFilter === 'inactive' && pm.isActive) return false

      // frequency
      const freq = (pm.schedule?.frequency || '').toLowerCase()
      if (freqFilter !== 'all' && freqFilter && freq !== freqFilter) {
        return false
      }

      // community
      if (communityFilter) {
        const code = pm.communityCode || pm.communityId
        if (code !== communityFilter) return false
      }

      // search
      if (q) {
        const haystack = [
          pm.name,
          pm.communityName,
          pm.communityCode,
          formatScheduleShort(pm.schedule)
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
        if (!haystack.includes(q)) return false
      }

      return true
    })
  }, [templates, statusFilter, freqFilter, communityFilter, searchText, showArchived])

  /* ---------- helpers ---------- */

  function applyFiltersToUrl(next = {}) {
    const params = new URLSearchParams(searchParams.toString())
    if (next.status !== undefined) params.set('status', next.status)
    if (next.freq !== undefined) params.set('freq', next.freq)
    if (next.communityId !== undefined) params.set('communityId', next.communityId)
    if (next.archived !== undefined) {
      if (next.archived) params.set('archived', '1')
      else params.delete('archived')
    }
    if (next.q !== undefined) {
      if (next.q) params.set('q', next.q)
      else params.delete('q')
    }
    setSearchParams(params)
  }

  function handleClearFilters() {
    setStatusFilter('all')
    setFreqFilter('all')
    setCommunityFilter('')
    setSearchText('')
    setShowArchived(false)
    setSearchParams({})
  }

  // open custom confirm dialog
  function openToggleConfirm(pm) {
    if (!pm?.id) return
    const makeInactive = !!pm.isActive
    if (makeInactive && adminOnlyArchive && !isAdmin) {
      setRestrictedArchiveTarget(pm)
      return
    }
    setConfirmToggleState({
      open: true,
      target: pm,
      makeInactive
    })
  }

  // user clicked "OK" in custom dialog
  async function handleConfirmToggle() {
    const { target: pm, makeInactive } = confirmToggleState
    if (!pm?.id) {
      setConfirmToggleState({ open: false, target: null, makeInactive: true })
      return
    }

    const label = makeInactive ? 'soft delete (archive)' : 'restore'

    try {
      await updatePmTemplateFS(pm.id, { isActive: !pm.isActive })
      setTemplates(prev =>
        (prev || []).map(row =>
          row.id === pm.id ? { ...row, isActive: !pm.isActive } : row
        )
      )
    } catch (e) {
      console.error('[PmList] toggle active failed', e)
      setErr(`Failed to ${label} PM template.`)
    } finally {
      setConfirmToggleState({ open: false, target: null, makeInactive: true })
    }
  }

  function closeConfirmDialog() {
    setConfirmToggleState(prev => ({ ...prev, open: false }))
  }

  async function handleRestrictedArchiveProceed() {
    const pm = restrictedArchiveTarget
    if (!pm?.id) {
      setRestrictedArchiveTarget(null)
      return
    }
    try {
      await createArchiveRequestTaskFS({
        entityType: 'PM template',
        entityTitle: pm.name || pm.title || pm.pmNumber || pm.id,
        communityCode: pm.communityCode || '',
        communityName: pm.communityName || pm.community || '',
        relatedUrl: `/pm/${encodeURIComponent(pm.id)}`,
        relatedLabel: pm.name || pm.pmNumber || 'PM Template',
      })
      setErr('Administrator notified of the archive request.')
    } catch (e) {
      console.error('[PmList] archive request failed', e)
      setErr(e?.message || 'Failed to notify administrator.')
    } finally {
      setRestrictedArchiveTarget(null)
    }
  }

  const confirmTitle = confirmToggleState.makeInactive
    ? 'Soft delete PM template?'
    : 'Restore PM template?'

  const confirmMessage = confirmToggleState.makeInactive
    ? 'Soft delete this PM template? It will no longer generate future PM work, but existing history is kept.'
    : 'Restore this PM template so it can generate future PM work again?'

  // EXPORT: download filtered PMs as Excel-friendly CSV
  function handleExport() {
    try {
      const rows = filteredTemplates || []
      if (!rows.length) {
        // nothing to export; silently do nothing or show a toast if you have one
        return
      }

      const csv = buildPmCsvRows(rows)
      const blob = new Blob([csv], {
        type: 'text/csv;charset=utf-8;'
      })
      const url = URL.createObjectURL(blob)

      const a = document.createElement('a')
      a.href = url
      // Excel opens CSV just fine
      a.download = 'pm-templates-export.csv'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (e) {
      console.error('[PmList] export failed', e)
      setErr('Failed to export PM templates.')
    }
  }

  /* ---------- render ---------- */

  return (
    <div>
      <h2
  style={{
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginTop: 14,
    marginBottom: 12,
  }}
>
  <span>Preventive Maintenance</span>
  <span style={{ flex: 1 }} />
  <button onClick={() => nav('/dev/pm/new')}>+ New PM</button>
</h2>

      {err && (
        <div
          className="card"
          style={{
            borderColor: '#b94a48',
            background: '#fff7f7',
            color: '#b94a48',
            marginBottom: 16
          }}
        >
          {err}
        </div>
      )}

      {/* Filter bar wrapper so Export can sit just outside top-right */}
      <div style={{ position: 'relative', marginBottom: 16 }}>
        {/* Export button just outside top-right of the card */}
        <button
          type="button"
          className="secondary"
          onClick={handleExport}
          style={{
            position: 'absolute',
            top: -56,      // adjust as needed to sit nicely above
            right: 0
          }}
        >
          Export CSV
        </button>

        {/* Filter bar (Status, Frequency, Community, Search) */}
        <div
          className="mobile-stack-grid card"
          style={{
            marginBottom: 0,
            display: 'grid',
            gridTemplateColumns: 'repeat(5, minmax(0, 1fr)) auto',
            gap: 12,
            alignItems: 'end'
          }}
        >
          <div>
            <label>Status</label>
            <select
              value={statusFilter}
              onChange={e => {
                const v = e.target.value
                setStatusFilter(v)
                if (v === 'inactive') {
                  setShowArchived(true)
                  applyFiltersToUrl({ status: v, archived: true })
                  return
                }
                applyFiltersToUrl({ status: v })
              }}
            >
              <option value="all">All statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div>
            <label>Frequency</label>
            <select
              value={freqFilter}
              onChange={e => {
                const v = e.target.value
                setFreqFilter(v)
                applyFiltersToUrl({ freq: v })
              }}
            >
              <option value="all">All frequencies</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="yearly">Yearly</option>
            </select>
          </div>

          <div>
            <label>Community</label>
            <select
              value={communityFilter}
              onChange={e => {
                const v = e.target.value
                setCommunityFilter(v)
                applyFiltersToUrl({ communityId: v })
              }}
            >
              <option value="">All my communities</option>
              {communityOptions.map(c => (
                <option key={c.code} value={c.code}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label>Search</label>
            <input
              type="text"
              placeholder="Search by name, community, or schedule"
              value={searchText}
              onChange={e => {
                const v = e.target.value
                setSearchText(v)
                applyFiltersToUrl({ q: v })
              }}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingBottom: 10 }}>
            <input
              id="pm-show-archived"
              type="checkbox"
              checked={showArchived}
              onChange={e => {
                const checked = e.target.checked
                setShowArchived(checked)
                if (!checked && statusFilter === 'inactive') {
                  setStatusFilter('all')
                  applyFiltersToUrl({ archived: false, status: 'all' })
                  return
                }
                applyFiltersToUrl({ archived: checked })
              }}
            />
            <label htmlFor="pm-show-archived" style={{ margin: 0 }}>Show archived</label>
          </div>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button
              type="button"
              className="secondary"
              onClick={handleClearFilters}
            >
              Clear
            </button>
            <button
              type="button"
              className="primary"
              onClick={() => nav('/pm/new')}
            >
              + New PM
            </button>
          </div>
        </div>
      </div>

      {/* List */}
      <div className="card">
<div className="responsive-table-scroll" tabIndex={0} role="region" aria-label="Scrollable table"><table className="table">
<colgroup>
  <col style={{ width: '30%' }} />  {/* Name */}
  <col style={{ width: '20%' }} />  {/* Community */}
  <col style={{ width: '10%' }} />  {/* Schedule */}
  <col style={{ width: '8%' }} />  {/* Status */}
  <col style={{ width: '14%' }} />  {/* Next */}
  <col style={{ width: '14%' }} />  {/* Actions (larger now) */}
</colgroup>

  <thead>
    <tr>
      <th>Name</th>
      <th>Community</th>
      <th>Schedule</th>
      <th style={{ textAlign: 'center' }}>Status</th>
      <th>Next</th>
      <th style={{ textAlign: 'center' }}>Actions</th>
    </tr>
  </thead>

  <tbody>
    {loading && (
      <tr>
        <td colSpan={6}>Loading…</td>
      </tr>
    )}

    {!loading && filteredTemplates.length === 0 && (
      <tr>
        <td colSpan={6}>No preventive maintenance items found.</td>
      </tr>
    )}

    {!loading &&
      filteredTemplates.map((pm) => (
        <tr key={pm.id}>

{/* Name (clickable, matches table text) */}
<td>
  <button
    type="button"
    onClick={() =>
      nav(
        `/pm/${encodeURIComponent(pm.id)}?communityId=${encodeURIComponent(
          pm.communityCode || ''
        )}`
      )
    }
    data-tooltip="Open PM"
    style={{
      background: 'transparent',
      border: 0,
      padding: 0,
      margin: 0,
      font: 'inherit',
      color: 'inherit',
      cursor: 'pointer',
      textAlign: 'left',
      lineHeight: 'inherit',
      display: 'inline',
    }}
    onMouseEnter={e => (e.currentTarget.style.opacity = '0.85')}
    onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
    onFocus={e => (e.currentTarget.style.opacity = '0.85')}
    onBlur={e => (e.currentTarget.style.opacity = '1')}
  >
    {pm.name || '(untitled)'}
  </button>
</td>

          {/* Community */}
          <td>
            {pm.communityName || pm.communityCode || ''}
          </td>

          {/* Schedule */}
          <td>
            {formatScheduleShort(pm.schedule)}
          </td>

          {/* Status – centered */}
          <td style={{ textAlign: 'center' }}>
            {pm.isActive ? 'Active' : 'Inactive'}
          </td>

          {/* Next – keep on one line */}
          <td style={{ whiteSpace: 'nowrap' }}>
            {pm.nextOccurrence
              ? formatDateTime(pm.nextOccurrence)
              : '—'}
          </td>

          {/* Actions – centered button group */}
<td
  style={{
    textAlign: 'center',
    whiteSpace: 'nowrap',
  }}
>
  <div
    style={{
      display: 'inline-flex',
      gap: 8,
      alignItems: 'center',
    }}
  >
    <button
      type="button"
      className="secondary"
      style={{ padding: '4px 10px' }}
      onClick={() => nav(`/pm/${encodeURIComponent(pm.id)}?communityId=${encodeURIComponent(pm.communityCode || '')}`)}
    >
      Open
    </button>

    <button
      type="button"
      className="secondary"
      style={{ padding: '4px 10px' }}
      onClick={() => openToggleConfirm(pm)}
    >
      {pm.isActive ? 'Delete' : 'Restore'}
    </button>
  </div>
</td>
        </tr>
      ))}
  </tbody>
</table></div>
      </div>

      {/* Custom confirm dialog for delete / restore */}
      {confirmToggleState.open && (
        <ConfirmDialog
          open={confirmToggleState.open}
          title={confirmTitle}
          message={confirmMessage}
          onCancel={closeConfirmDialog}
          onConfirm={handleConfirmToggle}
        />
      )}

      {restrictedArchiveTarget && (
        <ConfirmDialog
          open={!!restrictedArchiveTarget}
          title="Archive Restricted"
          message={
            <div>
              <p style={{ marginTop: 0 }}>
                Archiving is restricted to Administrators. If you need this PM template deleted or archived,
                please contact an administrator.
              </p>
              <p style={{ marginBottom: 0 }}>
                If you continue, MaintainIQ will create an admin task with the details of your request.
              </p>
            </div>
          }
          confirmLabel="Notify Admin"
          tone="default"
          onCancel={() => setRestrictedArchiveTarget(null)}
          onConfirm={handleRestrictedArchiveProceed}
        />
      )}
    </div>
  )
}
